<template>
  <section class="card-block card-block--highlight storyboard-shell">
    <div class="block-header block-header--stack-mobile">
      <div>
        <h2>分镜工作台</h2>
        <span class="block-tip">分镜剧本负责阅读和修改镜头，一致性资产负责沉淀角色/场景/道具官方参考，分镜生成负责处理图片与视频素材</span>
      </div>
      <div class="result-actions">
        <el-button v-if="storyboardWorkspaceTab !== 'consistency'" plain :loading="storyboardSaving" @click="emit('save-storyboard-scenes')">保存分镜</el-button>
        <el-button v-else type="primary" plain :loading="consistencySaving" @click="emit('save-consistency-assets')">保存一致性资产</el-button>
        <el-button
          v-if="isPendingApproval && storyboardWorkspaceTab !== 'consistency'"
          type="primary"
          :loading="storyboardSaving || acting"
          @click="emit('save-and-regenerate-storyboard')"
        >
          保存后重新生成
        </el-button>
      </div>
    </div>

    <div class="workspace-switcher">
      <button
        type="button"
        class="workspace-switcher__tab"
        :class="{ 'workspace-switcher__tab--active': storyboardWorkspaceTab === 'script' }"
        @click="emit('update:storyboardWorkspaceTab', 'script')"
      >
        分镜剧本
      </button>
      <button
        type="button"
        class="workspace-switcher__tab"
        :class="{ 'workspace-switcher__tab--active': storyboardWorkspaceTab === 'consistency' }"
        @click="emit('update:storyboardWorkspaceTab', 'consistency')"
      >
        一致性资产设定
      </button>
      <button
        type="button"
        class="workspace-switcher__tab"
        :class="{ 'workspace-switcher__tab--active': storyboardWorkspaceTab === 'generation' }"
        @click="emit('update:storyboardWorkspaceTab', 'generation')"
      >
        分镜生成
      </button>
    </div>

    <StoryboardScriptWorkbench
      v-if="storyboardWorkspaceTab === 'script'"
      :scenes="storyboardDraftScenes"
      :active-scene-number="storyboardVisualActiveSceneNumber"
      :editing-scene-number="storyboardEditingSceneNumber"
      :selection-options="consistencyAssetSelectionOptions"
      :build-scene-meta="buildSceneMeta"
      :build-scene-binding-labels="buildSceneBindingLabels"
      @add-scene="emit('add-storyboard-scene')"
      @insert-scene="emit('insert-storyboard-scene', $event)"
      @hover-scene="emit('update:hoveredStoryboardSceneNumber', $event)"
      @select-scene="emit('update:selectedStoryboardSceneNumber', $event)"
      @toggle-edit="emit('toggle-storyboard-scene-edit', $event)"
      @delete-scene="emit('delete-storyboard-scene', $event)"
      @mark-dirty="emit('mark-storyboard-draft-dirty')"
      @cancel-edit="emit('cancel-storyboard-scene-edit')"
      @finish-edit="emit('finish-storyboard-scene-edit')"
    />

    <ConsistencyWorkbench
      v-else-if="storyboardWorkspaceTab === 'consistency'"
      :project-id="projectId"
      :consistency-saving="consistencySaving"
      :consistency-asset-tab="consistencyAssetTab"
      :consistency-asset-stats="consistencyAssetStats"
      :active-consistency-assets="activeConsistencyAssets"
      :consistency-asset-image-generating="consistencyAssetImageGenerating"
      :consistency-asset-image-uploading="consistencyAssetImageUploading"
      :format-scene-image-source="formatSceneImageSource"
      @update:consistency-asset-tab="emit('update:consistencyAssetTab', $event)"
      @add-asset="emit('add-consistency-asset', $event)"
      @save-assets="emit('save-consistency-assets')"
      @asset-input="emit('mark-consistency-dirty')"
      @open-knowledge-base="emit('open-knowledge-base-for-consistency-asset', $event)"
      @generate-reference="emit('generate-consistency-asset-reference', $event)"
      @upload-asset="emit('upload-consistency-asset', $event)"
      @clear-image="emit('clear-consistency-asset-image', $event)"
      @remove-asset="emit('remove-consistency-asset', $event)"
    />

    <StoryboardGenerationWorkspace
      v-else
      :generation-tab="storyboardGenerationTab"
      :project-id="projectId"
      :scene="selectedStoryboardSceneDraft"
      :selected-image-url="selectedStoryboardImageUrl"
      :selected-video-url="selectedStoryboardVideoUrl"
      :selected-uploaded-video="selectedStoryboardUploadedVideo"
      :selected-video-error="selectedStoryboardVideoError"
      :preview-overlay-visible="selectedScenePreviewOverlayVisible"
      :preview-overlay-title="selectedScenePreviewOverlayTitle"
      :preview-overlay-description="selectedScenePreviewOverlayDescription"
      :preview-overlay-note="selectedScenePreviewOverlayNote"
      :scene-navigator-items="storyboardGenerationSceneItems"
      :bound-assets="selectedStoryboardBoundAssets"
      :selected-scene-number="selectedStoryboardSceneNumber"
      :mention-items="selectedStoryboardPromptMentionItems"
      :storyboard-saving="storyboardSaving"
      :scene-video-task="selectedSceneVideoTask"
      :scene-video-task-description="selectedSceneVideoTaskDescription"
      :scene-video-task-status-label="selectedSceneVideoTaskStatusLabel"
      :scene-video-task-timeline="selectedSceneVideoTaskTimeline"
      :scene-video-task-facts="selectedSceneVideoTaskFacts"
      :scene-video-feedback-drafts="sceneVideoFeedbackDrafts"
      :scene-video-loading="sceneVideoLoading"
      :scene-video-saving="sceneVideoSaving"
      :is-scene-image-loading="isSceneImageLoading"
      :is-scene-image-busy="isSceneImageBusy"
      :is-scene-video-generating="isSceneVideoGenerating"
      @update:generation-tab="emit('update:storyboardGenerationTab', $event)"
      @mark-dirty="emit('mark-storyboard-draft-dirty')"
      @create-mention-asset="emit('create-mention-asset', $event)"
      @select-scene="emit('update:selectedStoryboardSceneNumber', $event)"
      @open-knowledge-base-for-scene="emit('open-knowledge-base-for-scene', $event)"
      @trigger-scene-file-upload="emit('trigger-scene-file-upload', $event)"
      @clear-scene-image="emit('clear-scene-image', $event)"
      @generate-scene-image="emit('generate-scene-image', $event)"
      @download-scene-image="emit('download-scene-image', $event)"
      @scene-video-loaded="emit('scene-video-loaded', $event)"
      @scene-video-error="emit('scene-video-error', $event)"
      @generate-scene-video="emit('generate-scene-video', $event)"
      @trigger-scene-video-upload="emit('trigger-scene-video-upload', $event)"
      @download-scene-video="emit('download-scene-video', $event)"
      @save-scene-video-feedback="emit('save-scene-video-feedback', $event)"
    />

    <section v-if="isPendingApproval || reviewingPreviousStage" class="workspace-approval workspace-approval--split">
      <div class="workspace-approval__copy">
        <h3>{{ reviewingPreviousStage ? '分镜重新确认' : '全部分镜确认' }}</h3>
        <p>{{ reviewingPreviousStage ? '当前先展示已生成的分镜旧结果；你确认后，系统才会清空成片阶段并重新生成。' : storyboardApprovalHint }}</p>
      </div>
      <div class="workspace-approval__actions workspace-approval__actions--end">
        <el-button class="workspace-approval__primary-button" type="primary" :loading="acting" :disabled="!canApproveCurrentStage" @click="emit('approve-from-workspace')">{{ approveButtonLabel }}</el-button>
      </div>
    </section>

    <section v-else class="workspace-approval workspace-approval--muted">
      <div class="workspace-approval__copy">
        <h3>当前状态</h3>
        <p>{{ sideGuidance }}</p>
      </div>
      <div v-if="showResumeAction" class="workspace-approval__actions">
        <el-button type="primary" :loading="acting" @click="emit('resume-from-workspace')">恢复并继续</el-button>
      </div>
    </section>
  </section>
</template>

<script setup lang="ts">
import type { ConsistencyAssetSelectionOptions } from './consistencyAssets'
import type { SceneImageAction } from './useSceneImageWorkspace'
import type {
  ConsistencyAssetDraft,
  ConsistencyAssetType,
  PromptMentionOption,
  StoryboardGenerationSceneNavigatorItem,
  StoryboardSceneDraft,
  UploadedWorkspaceVideo,
} from './types'

import ConsistencyWorkbench from './ConsistencyWorkbench.vue'
import StoryboardGenerationWorkspace from './StoryboardGenerationWorkspace.vue'
import StoryboardScriptWorkbench from './StoryboardScriptWorkbench.vue'

type StoryboardWorkspaceTab = 'script' | 'consistency' | 'generation'
type StoryboardBoundAssets = {
  character: ConsistencyAssetDraft[]
  scene: ConsistencyAssetDraft[]
  prop: ConsistencyAssetDraft[]
}

defineProps<{
  projectId: number
  inspectableStageOptions: Array<{ key: string; label: string }>
  currentStage: string
  previousInspectableStage: string | null
  nextInspectableStage: string | null
  storyboardWorkspaceTab: StoryboardWorkspaceTab
  storyboardSaving: boolean
  acting: boolean
  consistencySaving: boolean
  isPendingApproval: boolean
  reviewingPreviousStage: boolean
  showResumeAction: boolean
  approveButtonLabel: string
  storyboardApprovalHint: string
  canApproveCurrentStage: boolean
  sideGuidance: string
  storyboardDraftScenes: StoryboardSceneDraft[]
  storyboardVisualActiveSceneNumber: number | null
  storyboardEditingSceneNumber: number | null
  consistencyAssetSelectionOptions: ConsistencyAssetSelectionOptions
  buildSceneMeta: (scene: StoryboardSceneDraft) => string[]
  buildSceneBindingLabels: (scene: StoryboardSceneDraft) => string[]
  consistencyAssetTab: ConsistencyAssetType
  consistencyAssetStats: Array<{ key: string; label: string; total: number; ready: number }>
  activeConsistencyAssets: ConsistencyAssetDraft[]
  consistencyAssetImageGenerating: Record<string, boolean>
  consistencyAssetImageUploading: Record<string, boolean>
  formatSceneImageSource: (source: string) => string
  storyboardGenerationTab: 'image' | 'video'
  selectedStoryboardSceneDraft: StoryboardSceneDraft | null
  selectedStoryboardImageUrl: string
  selectedStoryboardVideoUrl: string
  selectedStoryboardUploadedVideo: UploadedWorkspaceVideo | null
  selectedStoryboardVideoError: string
  selectedScenePreviewOverlayVisible: boolean
  selectedScenePreviewOverlayTitle: string
  selectedScenePreviewOverlayDescription: string
  selectedScenePreviewOverlayNote: string
  storyboardGenerationSceneItems: StoryboardGenerationSceneNavigatorItem[]
  selectedStoryboardBoundAssets: StoryboardBoundAssets
  selectedStoryboardSceneNumber: number | null
  selectedStoryboardPromptMentionItems: PromptMentionOption[]
  selectedSceneVideoTask: Record<string, any> | null
  selectedSceneVideoTaskDescription: string
  selectedSceneVideoTaskStatusLabel: string
  selectedSceneVideoTaskTimeline: Array<{ label: string; state: string }>
  selectedSceneVideoTaskFacts: Array<{ label: string; value: string }>
  sceneVideoFeedbackDrafts: Record<number, string>
  sceneVideoLoading: Record<number, boolean>
  sceneVideoSaving: Record<number, boolean>
  isSceneImageLoading: (sceneNumber: number, action: SceneImageAction) => boolean
  isSceneImageBusy: (sceneNumber: number) => boolean
  isSceneVideoGenerating: (sceneNumber: number) => boolean
}>()

const emit = defineEmits<{
  (event: 'update:storyboardWorkspaceTab', value: StoryboardWorkspaceTab): void
  (event: 'switch-stage', value: string): void
  (event: 'save-storyboard-scenes'): void
  (event: 'save-consistency-assets'): void
  (event: 'save-and-regenerate-storyboard'): void
  (event: 'add-storyboard-scene'): void
  (event: 'insert-storyboard-scene', payload: { sceneNumber: number; position: 'before' | 'after' }): void
  (event: 'update:hoveredStoryboardSceneNumber', value: number | null): void
  (event: 'update:selectedStoryboardSceneNumber', value: number): void
  (event: 'toggle-storyboard-scene-edit', value: number): void
  (event: 'delete-storyboard-scene', value: number): void
  (event: 'mark-storyboard-draft-dirty'): void
  (event: 'cancel-storyboard-scene-edit'): void
  (event: 'finish-storyboard-scene-edit'): void
  (event: 'update:consistencyAssetTab', value: ConsistencyAssetType): void
  (event: 'add-consistency-asset', value: ConsistencyAssetType): void
  (event: 'mark-consistency-dirty'): void
  (event: 'open-knowledge-base-for-consistency-asset', payload: { type: ConsistencyAssetType; id: string }): void
  (event: 'generate-consistency-asset-reference', payload: { type: ConsistencyAssetType; id: string }): void
  (event: 'upload-consistency-asset', payload: { type: ConsistencyAssetType; id: string }): void
  (event: 'clear-consistency-asset-image', payload: { type: ConsistencyAssetType; id: string }): void
  (event: 'remove-consistency-asset', payload: { type: ConsistencyAssetType; id: string }): void
  (event: 'update:storyboardGenerationTab', value: 'image' | 'video'): void
  (event: 'create-mention-asset', payload: { name: string; sceneNumber?: number }): void
  (event: 'download-scene-image', sceneNumber: number): void
  (event: 'open-knowledge-base-for-scene', sceneNumber: number): void
  (event: 'trigger-scene-file-upload', sceneNumber: number): void
  (event: 'clear-scene-image', sceneNumber: number): void
  (event: 'generate-scene-image', payload: { sceneNumber: number; imageModelId?: string }): void
  (event: 'scene-video-loaded', sceneNumber: number): void
  (event: 'scene-video-error', sceneNumber: number): void
  (event: 'generate-scene-video', payload: { sceneNumber: number; videoModelId?: string }): void
  (event: 'trigger-scene-video-upload', sceneNumber: number): void
  (event: 'download-scene-video', sceneNumber: number): void
  (event: 'save-scene-video-feedback', sceneNumber: number): void
  (event: 'approve-from-workspace'): void
  (event: 'resume-from-workspace'): void
}>()
</script>

<style scoped>
.storyboard-shell {
  display: grid;
  gap: 24px;
  background:
    radial-gradient(circle at top left, rgba(16, 185, 129, 0.12), transparent 24%),
    radial-gradient(circle at top right, rgba(96, 165, 250, 0.12), transparent 22%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.99), rgba(245, 249, 255, 0.98));
  border-color: rgba(191, 219, 254, 0.36);
  box-shadow: 0 30px 70px rgba(15, 23, 42, 0.08);
}

.card-block {
  padding: 24px;
  border-radius: 24px;
  background: #fff;
  border: 1px solid rgba(148, 163, 184, 0.18);
}

.card-block--highlight {
  background: linear-gradient(180deg, rgba(255, 247, 237, 0.82), rgba(255, 255, 255, 0.98));
}

.block-header,
.result-actions,
.workspace-approval__actions,
.workspace-switcher {
  display: flex;
  gap: 12px;
}

.block-header {
  justify-content: space-between;
  padding: 16px 18px;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.72);
  border: 1px solid rgba(226, 232, 240, 0.78);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.92);
}

.block-header--stack-mobile {
  align-items: flex-start;
  flex-wrap: wrap;
}

.block-header h2,
.workspace-approval__copy h3 {
  margin: 0;
  color: #10243d;
  letter-spacing: -0.02em;
}

.block-tip {
  color: #0369a1;
  font-size: 12px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  font-weight: 700;
}

.result-actions {
  flex-wrap: wrap;
  justify-content: flex-end;
  align-items: center;
}

.workspace-switcher {
  position: relative;
  align-items: center;
  width: max-content;
  max-width: 100%;
  padding: 10px;
  border-radius: 999px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.95), rgba(246, 250, 255, 0.98));
  border: 1px solid rgba(191, 219, 254, 0.58);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.9), 0 16px 40px rgba(15, 23, 42, 0.07);
}

.workspace-switcher::before {
  content: '';
  position: absolute;
  inset: 8px;
  border-radius: 999px;
  background: linear-gradient(90deg, rgba(226, 232, 240, 0.3), rgba(240, 249, 255, 0.2));
  pointer-events: none;
}

.workspace-switcher__tab {
  position: relative;
  z-index: 1;
  border: 0;
  min-height: 46px;
  padding: 0 22px;
  border-radius: 999px;
  background: transparent;
  color: #475569;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
}

.workspace-switcher__tab:hover {
  color: #10243d;
  background: rgba(255, 255, 255, 0.6);
}

.workspace-switcher__tab--active {
  background: linear-gradient(135deg, #12c2a2, #0ea5a4);
  color: #f8fffd;
  box-shadow: 0 12px 28px rgba(20, 184, 166, 0.24);
}

.workspace-approval {
  display: grid;
  gap: 16px;
  padding: 24px;
  border-radius: 28px;
  background:
    radial-gradient(circle at top right, rgba(110, 231, 183, 0.12), transparent 28%),
    linear-gradient(135deg, rgba(240, 253, 250, 0.96), rgba(239, 246, 255, 0.94));
  border: 1px solid rgba(191, 219, 254, 0.42);
  box-shadow: 0 22px 44px rgba(15, 23, 42, 0.06);
}

.workspace-approval--split {
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
}

.workspace-approval--muted {
  background: linear-gradient(180deg, rgba(248, 250, 252, 0.98), rgba(239, 246, 255, 0.94));
}

.workspace-approval__copy {
  display: grid;
  gap: 8px;
}

.workspace-approval__copy p {
  margin: 0;
  color: #526173;
  line-height: 1.7;
}

.workspace-approval__copy h3 {
  font-size: 24px;
}

.workspace-approval__actions {
  flex-wrap: wrap;
}

.workspace-approval__actions--end {
  justify-content: flex-end;
  align-items: center;
}

.workspace-approval__primary-button {
  min-width: 244px;
  min-height: 56px;
  border-radius: 18px;
  box-shadow: 0 18px 30px rgba(37, 99, 235, 0.18);
}

@media (max-width: 1080px) {
  .workspace-approval--split {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .block-header,
  .result-actions,
  .workspace-approval__actions {
    flex-direction: column;
    align-items: stretch;
  }

  .workspace-switcher {
    width: 100%;
    justify-content: space-between;
  }

  .workspace-approval__actions--end {
    justify-content: stretch;
  }

  .workspace-approval__primary-button {
    width: 100%;
  }
}
</style>